import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Chip,
  TablePagination,
} from '@mui/material'
import { Trans, t } from '@lingui/macro'

import { useGetMedicalActStatisticsQuery, useGetMedicalActStatisticsByPerformerQuery, useGetMedicalActDetailedRecordsQuery } from '@/core/services/medical-act'
import { formatPrice } from '@/utils/formatPrice'

type MedicalActsStatsComponentProps = {
  establishmentId: string
  startDate?: Date | null
  endDate?: Date | null
}

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100]

export function MedicalActsStatsComponent({ establishmentId, startDate, endDate }: MedicalActsStatsComponentProps) {
  const { t: translate } = useTranslation()
  const [activeTab, setActiveTab] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const queryParams = {
    startDate: startDate?.toISOString(),
    endDate: endDate?.toISOString(),
  }

  // Fetch all three data sources
  const { data: statsByType, isLoading: loadingByType, error: errorByType } = useGetMedicalActStatisticsQuery(queryParams)
  const { data: statsByPerformer, isLoading: loadingByPerformer, error: errorByPerformer } = useGetMedicalActStatisticsByPerformerQuery(queryParams)
  const {
    data: detailedRecords,
    isLoading: loadingDetailed,
    error: errorDetailed,
  } = useGetMedicalActDetailedRecordsQuery({
    ...queryParams,
    limit: rowsPerPage,
    skip: page * rowsPerPage,
  })

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
    setPage(0) // Reset pagination when switching tabs
  }

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString()
  }

  // Loading state
  if (loadingByType && activeTab === 0) {
    return <LoadingSpinner />
  }

  // Error state
  if (errorByType && activeTab === 0) {
    return <ErrorAlert message={translate('common.error.loading', { defaultValue: 'Error loading data' })} />
  }

  // Empty state (only for summary tab)
  if (activeTab === 0 && (!statsByType || statsByType.statistics.length === 0)) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {translate('page.admin.statistics.medicalActs.empty', { defaultValue: 'No medical acts recorded in this period' })}
        </Typography>
      </Paper>
    )
  }

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
            <Typography variant="h4">{statsByType?.statistics.reduce((sum, s) => sum + s.count, 0) ?? 0}</Typography>
            <Typography variant="body2">
              <Trans>Total Acts</Trans>
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.main', color: 'white' }}>
            <Typography variant="h4">{formatPrice(statsByType?.totalRevenue ?? 0)}</Typography>
            <Typography variant="body2">
              <Trans>Total Revenue</Trans>
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.main', color: 'white' }}>
            <Typography variant="h4">{statsByPerformer?.statistics.length ?? 0}</Typography>
            <Typography variant="body2">
              <Trans>Performers</Trans>
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
          <Tab label={t`By Act Type`} />
          <Tab label={t`By Performer`} />
          <Tab label={t`Detailed Records`} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && <ByActTypeTable statistics={statsByType?.statistics ?? []} />}

      {activeTab === 1 &&
        (loadingByPerformer ? (
          <LoadingSpinner />
        ) : errorByPerformer ? (
          <ErrorAlert message={translate('common.error.loading')} />
        ) : (
          <ByPerformerTable statistics={statsByPerformer?.statistics ?? []} />
        ))}

      {activeTab === 2 &&
        (loadingDetailed ? (
          <LoadingSpinner />
        ) : errorDetailed ? (
          <ErrorAlert message={translate('common.error.loading')} />
        ) : (
          <>
            <DetailedRecordsTable records={detailedRecords?.records ?? []} formatDate={formatDate} />
            <TablePagination
              component="div"
              count={detailedRecords?.total ?? 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            />
          </>
        ))}
    </Box>
  )
}

// ==================== Sub-components ====================

const LoadingSpinner = () => (
  <Box display="flex" justifyContent="center" p={4}>
    <CircularProgress />
  </Box>
)

const ErrorAlert = ({ message }: { message: string }) => <Alert severity="error">{message}</Alert>

type ByActTypeTableProps = {
  statistics: Array<{ templateId: string; templateName: string; templateCode: string; count: number; totalRevenue: number }>
}

const ByActTypeTable = ({ statistics }: ByActTypeTableProps) => (
  <Paper>
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <Trans>Act Type</Trans>
            </TableCell>
            <TableCell>
              <Trans>Code</Trans>
            </TableCell>
            <TableCell align="right">
              <Trans>Count</Trans>
            </TableCell>
            <TableCell align="right">
              <Trans>Revenue</Trans>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {statistics.map((stat) => (
            <TableRow key={stat.templateId}>
              <TableCell>{stat.templateName}</TableCell>
              <TableCell>
                <code>{stat.templateCode}</code>
              </TableCell>
              <TableCell align="right">{stat.count}</TableCell>
              <TableCell align="right">{formatPrice(stat.totalRevenue)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
)

type ByPerformerTableProps = {
  statistics: Array<{ performerId: string; performerName: string; performerEmail: string; count: number; totalRevenue: number }>
}

const ByPerformerTable = ({ statistics }: ByPerformerTableProps) => (
  <Paper>
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <Trans>Performer</Trans>
            </TableCell>
            <TableCell>
              <Trans>Email</Trans>
            </TableCell>
            <TableCell align="right">
              <Trans>Acts Performed</Trans>
            </TableCell>
            <TableCell align="right">
              <Trans>Total Revenue</Trans>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {statistics.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                <Typography color="text.secondary">
                  <Trans>No data available</Trans>
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            statistics.map((stat) => (
              <TableRow key={stat.performerId}>
                <TableCell>{stat.performerName}</TableCell>
                <TableCell>{stat.performerEmail}</TableCell>
                <TableCell align="right">{stat.count}</TableCell>
                <TableCell align="right">{formatPrice(stat.totalRevenue)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
)

type DetailedRecord = {
  _id: string
  performedAt: string
  priceAtTime: number
  performerName: string
  templateName: string
  templateCode: string
  patientName: string
  consultationId?: string
  expertiseId?: string
}

type DetailedRecordsTableProps = {
  records: DetailedRecord[]
  formatDate: (dateStr: string) => string
}

const DetailedRecordsTable = ({ records, formatDate }: DetailedRecordsTableProps) => (
  <Paper>
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <Trans>Date</Trans>
            </TableCell>
            <TableCell>
              <Trans>Performer</Trans>
            </TableCell>
            <TableCell>
              <Trans>Act</Trans>
            </TableCell>
            <TableCell>
              <Trans>Patient</Trans>
            </TableCell>
            <TableCell>
              <Trans>Context</Trans>
            </TableCell>
            <TableCell align="right">
              <Trans>Price</Trans>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography color="text.secondary">
                  <Trans>No records found</Trans>
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            records.map((record) => (
              <TableRow key={record._id}>
                <TableCell>{formatDate(record.performedAt)}</TableCell>
                <TableCell>{record.performerName}</TableCell>
                <TableCell>
                  {record.templateName}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    ({record.templateCode})
                  </Typography>
                </TableCell>
                <TableCell>{record.patientName}</TableCell>
                <TableCell>
                  {record.consultationId && <Chip size="small" label={t`Consultation`} color="primary" variant="outlined" />}
                  {record.expertiseId && <Chip size="small" label={t`Expertise`} color="secondary" variant="outlined" />}
                  {!record.consultationId && !record.expertiseId && <Chip size="small" label={t`Standalone`} variant="outlined" />}
                </TableCell>
                <TableCell align="right">{formatPrice(record.priceAtTime)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
)

export default MedicalActsStatsComponent
